import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { ApiError } from '../lib/api'
import { useAuthedSession, useSession } from '../lib/SessionContext'
import { PasswordInput } from '../lib/PasswordInput'
import { PASSWORD_MIN_LENGTH, changePassword } from '../lib/session'

// เช็คฝั่งหน้าเว็บเพื่อบอกผู้ใช้ทันทีตอนพิมพ์ — backend เช็คซ้ำอยู่ ห้ามเชื่อฝั่ง client
const MIN_LENGTH = PASSWORD_MIN_LENGTH

export default function ChangePassword() {
  const { refresh, signIn } = useSession()
  const session = useAuthedSession()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const mustChange = session.kind === 'admin' && session.mustChangePassword

  const tooShort = next.length > 0 && next.length < MIN_LENGTH
  const sameAsOld = next.length > 0 && next === current
  const mismatch = confirm.length > 0 && confirm !== next
  const canSubmit =
    !submitting &&
    current.length > 0 &&
    next.length >= MIN_LENGTH &&
    confirm === next &&
    next !== current

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!canSubmit) return

    setSubmitting(true)
    setError(null)
    setDone(false)
    try {
      const res = await changePassword(current, next)
      setCurrent('')
      setNext('')
      setConfirm('')
      setDone(true)

      // ต้องอัปเดต session เพื่อล้างธง mustChangePassword ไม่งั้นเมนูอื่นยังถูกล็อกอยู่
      if (res.session) signIn(res.session)
      else await refresh()
    } catch (err) {
      // 401 ที่นี่คือ session หมดอายุจริง ๆ เท่านั้น (รหัสเดิมผิด backend ตอบ 400)
      // → เคลียร์ session ให้ router พาไปหน้า login ไม่ต้องขึ้น error ค้างในฟอร์มที่กำลังจะหายไป
      if (err instanceof ApiError && err.status === 401) {
        await refresh().catch(() => {})
        return
      }
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <h1>เปลี่ยนรหัสผ่าน</h1>

      {mustChange && (
        <p className="warn">
          บัญชีนี้ต้องเปลี่ยนรหัสผ่านก่อนใช้งานเมนูอื่น (ยังเป็นรหัสที่ผู้ดูแลระบบตั้งให้)
        </p>
      )}

      {done && (
        <div className="ok">
          <strong>เปลี่ยนรหัสผ่านเรียบร้อย</strong>
          <p>
            ครั้งต่อไปให้เข้าระบบด้วยรหัสใหม่ · <Link to="/">กลับหน้าแรก</Link>
          </p>
        </div>
      )}

      <section className="card form-card">
        <form onSubmit={submit}>
          <label>
            รหัสผ่านเดิม
            <PasswordInput
              value={current}
              onChange={setCurrent}
              autoComplete="current-password"
              required
            />
          </label>

          <label>
            รหัสผ่านใหม่
            <PasswordInput
              value={next}
              onChange={setNext}
              autoComplete="new-password"
              minLength={MIN_LENGTH}
              required
            />
            {tooShort ? (
              <small className="bad">ต้องยาวอย่างน้อย {MIN_LENGTH} ตัวอักษร</small>
            ) : sameAsOld ? (
              <small className="bad">ต้องไม่ซ้ำกับรหัสผ่านเดิม</small>
            ) : (
              <small className="hint">อย่างน้อย {MIN_LENGTH} ตัวอักษร</small>
            )}
          </label>

          <label>
            รหัสผ่านใหม่อีกครั้ง
            <PasswordInput
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              required
            />
            {mismatch && <small className="bad">กรอกไม่ตรงกับรหัสผ่านใหม่</small>}
          </label>

          {error && <p className="error">{error}</p>}

          <button type="submit" disabled={!canSubmit}>
            {submitting ? 'กำลังบันทึก…' : 'บันทึกรหัสผ่านใหม่'}
          </button>
        </form>
      </section>
    </>
  )
}
